import { useOrg } from "@/context/OrgContext";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Building2, Check, ChevronDown } from "lucide-react";
import { toast } from "sonner";

export default function OrgSwitcher() {
  const { memberships, activeOrg, activeOrgId, setActiveOrg } = useOrg();

  if (!memberships || memberships.length === 0) return null;

  const handleSwitch = async (orgId) => {
    if (orgId === activeOrgId) return;
    try {
      await setActiveOrg(orgId);
      toast.success("Organisation active modifiée");
      // Reload pages whose data is org-scoped (P3 transition: simpler to refresh)
      window.location.reload();
    } catch (e) {
      toast.error("Impossible de changer d'organisation");
    }
  };

  // Single membership → no dropdown, just a label badge
  if (memberships.length === 1) {
    return (
      <div
        className="hidden md:flex items-center gap-2 px-3 h-9 rounded-md border border-border bg-muted/40 text-sm text-foreground/80"
        data-testid="org-switcher-single"
        title={activeOrg?.name}
      >
        <Building2 className="h-4 w-4 text-primary" />
        <span className="font-medium truncate max-w-[180px]">{activeOrg?.name}</span>
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-9 gap-2"
          data-testid="org-switcher-trigger"
        >
          <Building2 className="h-4 w-4 text-primary" />
          <span className="font-medium truncate max-w-[160px]">
            {activeOrg?.name || "Organisation"}
          </span>
          <ChevronDown className="h-4 w-4 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        <DropdownMenuLabel>Vos organisations</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {memberships.map((m) => {
          const org = m.organization;
          const isActive = org.id === activeOrgId;
          const role = m.membership.org_role;
          return (
            <DropdownMenuItem
              key={org.id}
              onClick={() => handleSwitch(org.id)}
              className="flex items-start gap-2 py-2"
              data-testid={`org-switcher-item-${org.id}`}
            >
              <Building2 className="h-4 w-4 mt-0.5 text-primary shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium truncate">{org.name}</span>
                  {isActive && <Check className="h-4 w-4 text-green-600 shrink-0" />}
                </div>
                <div className="text-xs text-muted-foreground capitalize">
                  {role === "admin" ? "Administrateur" : "Membre"} · {org.plan}
                </div>
              </div>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
